import {hasText} from '@payloadcms/richtext-lexical/shared';

import {isDefined} from 'Application/utils/typeGuards';

import type {LexicalContent} from '@nfq/payload-lexical-parser';
import type {ModuleIntro} from 'types/payload-types';


/**
 * A custom hook that extracts and returns structured intro content from a ModuleIntro object.
 * This hook provides a safe way to access headline, subline, and text properties from an intro module,
 * handling cases where the intro might be null or undefined by returning the individual properties directly.
 *
 * @param intro The intro module object containing headline, subline, and text content, or null/undefined.
 * @returns An object containing the extracted headline, subline, and text properties from the intro.
 *
 * @example
 * ```tsx
 * const introModule = {
 *   headline: 'Welcome to our site',
 *   subline: 'Discover amazing content',
 *   text: lexicalContent
 * };
 *
 * const { headline, subline, text } = useBlockIntro(introModule);
 * ```
 */
export const useBlockIntro = (intro?: ModuleIntro | null) => {
    let headline: string | null | undefined;
    let subline: string | null | undefined;
    let text: LexicalContent | null | undefined;

    if (isDefined(intro)) {
        headline = intro.headline;
        subline = intro.subline;
        text = hasText(intro.text) ? intro.text : null;
    }

    return {
        headline,
        subline,
        text
    };
};